import React, {
    useEffect,
    useState,
    useMemo
} from 'react';
import {
    getCurrentUserRole,
    getCurrentUserId
} from '../utils/authHelper';
import AbsenceCalendar from './AbsenceCalendar';
import {
    getHolidayEntries,
    createHoliday,
    updateHoliday,
    deleteHoliday as removeHoliday,
} from '../services/HolidayService';
import {

    getProjectStats
} from '../services/ProjectServices';
import {
    getOvertimeBalances,
    getTotalOvertimeBalance
} from '../services/OvertimeAccountService'
import {
    createSickDay,
    getSickEntries,
    updateSickDay,
    deleteSickDay as removeSickDay
} from '../services/SickDayService';
import '../styles/EmployeeArea.css';

import {getAllUsers} from "../services/UserService";
import {getCurrentMonthRange} from "../utils/dateHandler";

export default function EmployeeArea() {
    const [holidayEntries, setHolidayEntries] = useState([]);
    const [sickEntries, setSickEntries] = useState([]);
    const [OvertimeAccountsEntries, setOvertimeAccountsEntries] = useState([]);
    const [TotalOvertimeBalance, setTotalOvertimeBalance] = useState([]);
    const [projectUserEntries, setProjectUserEntries] = useState([]);
    const [expandedProjects, setExpandedProjects] = useState([]);

    const [holidayDateStart, setHolidayDateStart] = useState('');
    const [holidayDateEnd, setHolidayDateEnd] = useState('');
    const [holidayNumberDays, setHolidayNumberDays] = useState('');

    const [sickDateStart, setSickDateStart] = useState('');
    const [sickDateEnd, setSickDateEnd] = useState('');
    const [sickDayNumberDays, setSickDayNumberDays] = useState('');

    const [editingHolidayId, setEditingHolidayId] = useState(null);
    const [editingSickId, setEditingSickId] = useState(null);

    const monthRange = getCurrentMonthRange();

    const [projectStatsUserStart, setProjectStatsUserStart] = useState(monthRange.start);
    const [projectStatsUserEnd, setProjectStatsUserEnd] = useState(monthRange.end);

    const [activeTab, setActiveTab] = useState('holiday');


    const userId = getCurrentUserId();
    const role = getCurrentUserRole();

    const [allUsers, setAllUsers] = useState([]);
    const [selectedUserId, setSelectedUserId] = useState(userId);

    useEffect(() => {
        if (role === 'admin') {
            void loadUsers();
        }
    }, []);

    useEffect(() => {
        void loadEntries();
    }, [selectedUserId]);

    useEffect(() => {
        if (projectStatsUserStart && projectStatsUserEnd) {
            void loadProjectStats();
        }
    }, []);

    const loadUsers = async () => {
        try {
            const users = await getAllUsers();
            setAllUsers(users);
        } catch (err) {
            console.error('Failed loading users', err);
        }
    };

    const toggleProjectExpanded = (projectId) => {
        setExpandedProjects(prev =>
            prev.includes(projectId)
                ? prev.filter(id => id !== projectId)
                : [...prev, projectId]
        );
    };

    const monthFormatter = useMemo(
        () => new Intl.DateTimeFormat('de-DE', {month: 'long'}),
        []
    );

    const loadEntries = async () => {
        try {
            const [holidays, sickDays, overtimeAccounts, totalOvertimeBalance] = await Promise.all([
                getHolidayEntries({role, userId: selectedUserId}),
                getSickEntries({role, userId: selectedUserId}),
                getOvertimeBalances({role, userId: selectedUserId}),
                getTotalOvertimeBalance({userId: selectedUserId}),
            ]);
            setHolidayEntries(holidays);
            setSickEntries(sickDays);
            setOvertimeAccountsEntries(overtimeAccounts);
            setTotalOvertimeBalance(totalOvertimeBalance);
        } catch (err) {
            console.error('Failed loading entries', err);
        }
    };

    const saveHoliday = async () => {
        if (!holidayDateStart || !holidayDateEnd || !holidayNumberDays) return;
        try {
            if (editingHolidayId) {
                await updateHoliday(editingHolidayId, {
                    UserId: userId,
                    startDate: holidayDateStart,
                    endDate: holidayDateEnd,
                    numberDays: holidayNumberDays
                });
            } else {
                await createHoliday({
                    startDate: holidayDateStart,
                    endDate: holidayDateEnd,
                    numberDays: holidayNumberDays,
                    UserId: userId
                });
            }
            setHolidayDateStart('');
            setHolidayDateEnd('');
            setHolidayNumberDays('');
            setEditingHolidayId(null);
            await loadEntries();
        } catch (err) {
            console.error('Failed saving holiday', err);
        }
    };

    const saveSickDay = async () => {
        if (!sickDateStart || !sickDateStart || !sickDayNumberDays) return;
        try {
            if (editingSickId) {
                await updateSickDay(editingSickId, {
                    userId: userId,
                    StartDate: sickDateStart,
                    EndDate: sickDateEnd,
                    NumberDays: sickDayNumberDays
                });
            } else {
                await createSickDay({
                    userId: userId,
                    StartDate: sickDateStart,
                    EndDate: sickDateEnd,
                    NumberDays: sickDayNumberDays
                });
            }
            setSickDateStart('');
            setSickDateEnd('');
            setSickDayNumberDays('');
            setEditingSickId(null);
            await loadEntries();
        } catch (err) {
            console.error('Failed saving sick day', err);
        }
    };


    const deleteHoliday = async (id) => {
        if (window.confirm('Urlaub löschen?')) {
            try {
                await removeHoliday(id);
                await loadEntries();
            } catch (err) {
                console.error('Failed deleting holiday', err);
            }
        }
    };


    const deleteSickDay = async (id) => {
        if (window.confirm('Krankheitstag löschen?')) {
            try {
                await removeSickDay(id);
                await loadEntries();
            } catch (err) {
                console.error('Failed deleting sick day', err);
            }
        }
    };

    const loadProjectStats = async () => {
        if (!projectStatsUserStart || !projectStatsUserEnd) return;
        try {
            const stats = await getProjectStats({
                userId: selectedUserId,
                startDate: projectStatsUserStart,
                endDate: projectStatsUserEnd
            });
            setProjectUserEntries(stats);
        } catch (err) {
            console.error('Failed loading project stats', err);
        }
    };

    const convertDurationToHHMM = (durationStr) => {
        if (!durationStr) return '-';

        const negative = durationStr.startsWith('-');
        const cleaned = negative ? durationStr.slice(1) : durationStr;

        let days = 0, hours = 0, minutes = 0;

        // Format: D.HH:MM:SS or HH:MM:SS
        const daySplit = cleaned.split('.');
        const timePart = daySplit.length === 2 ? daySplit[1] : daySplit[0];
        if (daySplit.length === 2) days = parseInt(daySplit[0]);

        const [h, m, _] = timePart.split(':').map(Number);
        hours += h;
        minutes = m;

        const totalHours = days * 24 + hours;
        const sign = negative ? '-' : '';
        return `${sign}${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };


    return (
        <div className="employee-area-wrapper">
            <div className="employee-area-content">
                <h2>Mitarbeiterbereich</h2>

                {role === 'admin' && (
                    <div style={{margin: '1rem 0'}}>
                        <label htmlFor="userSelect"><strong>Mitarbeiter wählen:</strong>{' '}</label>

                        <select
                            id="userSelect"
                            value={selectedUserId}
                            onChange={(e) => setSelectedUserId(e.target.value)}>
                            {allUsers.map(user => (
                                <option key={user.id} value={user.id}>
                                    {user.displayName || user.name || user.email}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="internal-nav">
                    <button
                        className={`internal-tab ${activeTab === 'holiday' ? 'active' : ''}`}
                        onClick={() => setActiveTab('holiday')}>
                        Urlaub
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'sick' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sick')}>
                        Krankheitstag
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'hours' ? 'active' : ''}`}
                        onClick={() => setActiveTab('hours')}>
                        Stundenkonto
                    </button>
                    <button
                        className={`internal-tab ${activeTab === 'project' ? 'active' : ''}`}
                        onClick={() => setActiveTab('project')}>
                        Projekte
                    </button>
                </div>

                {activeTab === 'project' && (
                    <div>
                        <h3>Projektstunden</h3>
                        <div style={{display: 'flex', gap: '8px', alignItems: 'center', width: '100%'}}>
                            <input
                                type="date"
                                value={projectStatsUserStart}
                                onChange={e => setProjectStatsUserStart(e.target.value)}
                                className="input"
                                style={{flex: 1}}
                            />
                            <input
                                type="date"
                                value={projectStatsUserEnd}
                                onChange={e => setProjectStatsUserEnd(e.target.value)}
                                className="input"
                                style={{flex: 1}}
                            />
                            <button
                                className="employee_area_btn"
                                onClick={loadProjectStats}
                                disabled={!projectStatsUserStart || !projectStatsUserEnd}
                                style={{flex: 1}}
                            >
                                Projektstunden laden
                            </button>
                        </div>

                        <div style={{marginTop: '1rem'}}>
                            {projectUserEntries.length > 0 ? (
                                <table className="holiday-table">
                                    <thead>
                                    <tr>
                                        <th>Projekt</th>
                                        <th>Firma</th>
                                        <th>Gesamtzeit</th>
                                        <th>Einträge</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {projectUserEntries.map(project => (
                                        <React.Fragment key={project.projectId}>
                                            <tr
                                                onClick={() => toggleProjectExpanded(project.projectId)}
                                                style={{cursor: 'pointer'}}
                                            >
                                                <td>{project.projectName}</td>
                                                <td>{project.companyName}</td>
                                                <td>{project.totalWorkTime}</td>
                                                <td>{project.entries.length}</td>
                                            </tr>

                                            {expandedProjects.includes(project.projectId) && (
                                                <tr>
                                                    <td colSpan={4}>
                                                        <table className="nested-entry-table">
                                                            <thead>
                                                            <tr>
                                                                <th>Datum</th>
                                                                <th>Start</th>
                                                                <th>Ende</th>
                                                                <th>Arbeitszeit</th>
                                                                <th>Pause</th>
                                                                <th>Reisezeit</th>
                                                                <th>Ort</th>
                                                                <th>Tätigkeit</th>
                                                                <th>Kommentar</th>
                                                            </tr>
                                                            </thead>
                                                            <tbody>
                                                            {project.entries.map(entry => (
                                                                <tr key={entry.id}>
                                                                    <td>{entry.date}</td>
                                                                    <td>{entry.startTime}</td>
                                                                    <td>{entry.endTime}</td>
                                                                    <td>{entry.workTime}</td>
                                                                    <td>{entry.breakMinutes} min</td>
                                                                    <td>{entry.travelMinutes} min</td>
                                                                    <td>{entry.location}</td>
                                                                    <td>{entry.activity}</td>
                                                                    <td>{entry.comment}</td>
                                                                </tr>
                                                            ))}
                                                            </tbody>
                                                        </table>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p>Keine Daten geladen.</p>
                            )}
                        </div>
                    </div>
                )}


                {activeTab === 'holiday' && (
                    <div>
                        <h3>Urlaub eintragen</h3>

                        <div style={{display: 'flex', gap: '8px', alignItems: 'center', width: '100%'}}>
                            <input
                                type="date"
                                value={holidayDateStart}
                                onChange={e => setHolidayDateStart(e.target.value)}
                                className="input"
                                style={{flex: 1}}/>
                            <input
                                type="date"
                                value={holidayDateEnd}
                                onChange={e => setHolidayDateEnd(e.target.value)}
                                className="input"
                                style={{flex: 1}}/>
                            <input
                                type="number"
                                value={holidayNumberDays}
                                onChange={e => setHolidayNumberDays(e.target.value)}
                                className="input"
                                style={{flex: 1}}/>
                            <button
                                onClick={saveHoliday}
                                className="employee_area_btn"
                                style={{flex: 1}}>
                                {editingHolidayId ? 'Aktualisieren' : 'Eintragen'}
                            </button>
                        </div>

                        <table className="holiday-table">
                            <thead>
                            <tr>
                                <th>Startdatum</th>
                                <th>Enddatum</th>
                                <th>Urlaubstage</th>
                                {role === 'admin' && <th>Mitarbeiter</th>}
                                <th>Aktion</th>
                            </tr>
                            </thead>
                            <tbody>
                            {holidayEntries
                                .filter(e => role !== 'admin' || e.userId === selectedUserId)
                                .sort((a, b) => new Date(b.startDate) - new Date(a.startDate))

                                .map(e => (
                                    <tr key={e.id}>
                                        <td>{new Date(e.startDate).toLocaleDateString()}</td>
                                        <td>{new Date(e.endDate).toLocaleDateString()}</td>
                                        <td>{e.numberDays}</td>
                                        {role === 'admin' && <td>{e.displayName}</td>}
                                        <td>
                                            <button className={"button-warning"} onClick={() => {
                                                setHolidayDateStart(e.startDate.slice(0, 10));
                                                setHolidayDateEnd(e.endDate.slice(0, 10));
                                                setHolidayNumberDays(e.numberDays);
                                                setEditingHolidayId(e.id);
                                            }}>Bearbeiten
                                            </button>
                                            <button className={"button-danger"}
                                                    onClick={() => deleteHoliday(e.id)}>Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}


                {activeTab === 'hours' && (
                    <div>
                        <h3>Stundenkonto</h3>

                        <p>Total: {TotalOvertimeBalance.totalBalance}</p>
                        <table className="holiday-table">
                            <thead>
                            <tr>
                                <th>Jahr</th>
                                <th>Monat</th>
                                {role === 'admin' && <th>Mitarbeiter</th>}
                                <th>Geplante Zeit</th>
                                <th>Geleistete Zeit</th>
                                <th>Stundenkonto</th>
                                <th>Manuelle Korrektion</th>
                                <th>Balance</th>
                            </tr>
                            </thead>
                            <tbody>
                            {OvertimeAccountsEntries
                                .filter(e => role !== 'admin' || e.userId === selectedUserId)
                                .sort((a, b) => {
                                    if (b.year !== a.year) return b.year - a.year;
                                    return b.month - a.month;
                                })
                                .map(overtimeAccount => (
                                    <tr key={overtimeAccount.id}>
                                        <td>{overtimeAccount.year}</td>
                                        <td>{monthFormatter.format(new Date(
                                            overtimeAccount.year,
                                            (overtimeAccount.month ?? 1) - 1,
                                            1
                                        ))}</td>
                                        {role === 'admin' && <td>{overtimeAccount.displayName}</td>}
                                        <td>{convertDurationToHHMM(overtimeAccount.plannedTime)}</td>
                                        <td>{convertDurationToHHMM(overtimeAccount.actualTime)}</td>
                                        <td>{convertDurationToHHMM(overtimeAccount.overtime)}</td>
                                        <td>{overtimeAccount.manualCorrection}</td>
                                        <td>{convertDurationToHHMM(overtimeAccount.finalBalance)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}


                {activeTab === 'sick' && (
                    <div>
                        <div className="section">

                            <h3>Krankheitstag eintragen</h3>
                            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                                <input
                                    type="date"
                                    className="input"
                                    value={sickDateStart}
                                    onChange={e => setSickDateStart(e.target.value)}
                                    style={{flex: 1}}
                                />
                                <input
                                    type="date"
                                    className="input"
                                    value={sickDateEnd}
                                    onChange={e => setSickDateEnd(e.target.value)}
                                    style={{flex: 1}}
                                />
                                <input
                                    type="number"
                                    value={sickDayNumberDays}
                                    onChange={e => setSickDayNumberDays(e.target.value)}
                                    className="input"
                                    style={{flex: 1}}/>
                                <button
                                    className="employee_area_btn"
                                    onClick={saveSickDay}
                                    style={{flex: 1}}
                                >
                                    {editingSickId ? 'Aktualisieren' : 'Eintragen'}
                                </button>
                            </div>
                        </div>

                        <table className="holiday-table">
                            <thead>
                            <tr>
                                <th>Startdatum</th>
                                <th>Enddatum</th>
                                <th>Krankheitstage</th>
                                {role === 'admin' && <th>Mitarbeiter</th>}
                                <th>Aktion</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sickEntries
                                .filter(e => role !== 'admin' || e.userId === selectedUserId)
                                .sort((a, b) => new Date(b.date) - new Date(a.date))
                                .map(e => (
                                    <tr key={e.id}>
                                        <td>{new Date(e.startDate).toLocaleDateString()}</td>
                                        <td>{new Date(e.endDate).toLocaleDateString()}</td>
                                        <td>{e.numberDays}</td>
                                        {role === 'admin' && <td>{e.displayName}</td>}
                                        <td>
                                            <button className={"button-warning"} onClick={() => {
                                                setSickDateStart(e.date.slice(0, 10));
                                                setSickDateEnd(e.date.slice(0, 10));
                                                setSickDayNumberDays(e.numberDays);
                                                setEditingSickId(e.id);
                                            }}>Bearbeiten
                                            </button>
                                            <button className={"button-danger"}
                                                    onClick={() => deleteSickDay(e.id)}>Löschen
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            </div>

            <div className="employee-area-calendar">
                <AbsenceCalendar/>
            </div>
        </div>

    );
}
